import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { scrollToId } from "./scroll";
import { trackEvent } from "./track";
import { isApplyEnabled, getApplyUrl } from "./applyUrl";
import { useI18n } from "../../i18n";

const HERO_IMG =
  "https://images.unsplash.com/photo-1646315026053-27cf64144dd8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBidXNpbmVzcyUyMGFic3RyYWN0JTIwZGFyayUyMGJsdWUlMjBnb2xkfGVufDB8fHx8MTc4NTk0NjAwOHww&ixlib=rb-4.1.0&q=85";

const lineVariant = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + 0.16 * i, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { t, lang } = useI18n();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const lines = t("hero.lines");

  return (
    <section id="hero" ref={ref} className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img src={HERO_IMG} alt="" className="w-full h-[120%] object-cover" />
        <div className="absolute inset-0" style={{ background: "rgba(15,32,39,0.78)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #0F2027 8%, transparent 60%)" }} />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pb-24 pt-44">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="font-heading text-gold text-sm tracking-[0.25em] uppercase mb-8"
          data-testid="hero-kicker"
        >
          {t("hero.kicker")}
        </motion.p>

        <h1
          key={lang}
          className="font-heading font-bold tracking-tighter text-4xl sm:text-5xl lg:text-7xl leading-[1.04] max-w-4xl"
          data-testid="hero-title"
        >
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span custom={i} variants={lineVariant} initial="hidden" animate="show" className="block">
                {i === lines.length - 1 ? <span className="text-gold">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="text-base md:text-lg text-[#8A9DB1] max-w-xl mt-8 leading-relaxed"
          data-testid="hero-subtitle"
        >
          {t("hero.subtitle")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="flex flex-col sm:flex-row gap-4 mt-12"
        >
          {isApplyEnabled() ? (
            <a
              href={getApplyUrl()}
              className="btn-gold inline-flex items-center justify-center gap-2 px-8 py-4 text-base"
              onClick={() => trackEvent("apply_cta_click", { placement: "hero", language: lang, mode: "vytali_lp" })}
              data-testid="hero-apply-cta"
            >
              {t("nav.apply")} <ArrowUpRight size={18} />
            </a>
          ) : (
            <button
              onClick={() => {
                trackEvent("apply_cta_click", { placement: "hero", language: lang, mode: "contact_fallback" });
                scrollToId("contact");
              }}
              className="btn-gold inline-flex items-center justify-center gap-2 px-8 py-4 text-base"
              data-testid="hero-apply-cta"
            >
              {t("nav.apply")} <ArrowDown size={18} />
            </button>
          )}
          <a
            href="https://vytalimarketing.com"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost inline-flex items-center justify-center gap-2 px-8 py-4 text-base"
            onClick={() => trackEvent("click_decouvrir_vytali", { location: "hero" })}
            data-testid="hero-vytali-cta"
          >
            {t("hero.vytaliCta")} <ArrowUpRight size={18} />
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
